import { useState } from "react";
import api from "../lib/api";
import { ShieldCheck, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const CertificateVerify = () => {
  const [num, setNum] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);
  const [transfer, setTransfer] = useState({ new_owner_name: "", new_owner_email: "" });

  const verify = async (e) => {
    e.preventDefault();
    setData(null); setError("");
    try {
      const { data: d } = await api.get(`/certificates/verify/${num.trim().toUpperCase()}`);
      setData(d);
    } catch (err) {
      setError(err.response?.data?.detail || "Certificate not found");
    }
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/certificates/transfer", { certificate_number: data.certificate_number, ...transfer });
      toast.success("Ownership transferred.");
      setTransferOpen(false);
      const { data: d } = await api.get(`/certificates/verify/${data.certificate_number}`);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign in required to transfer ownership.");
    }
  };

  return (
    <div data-testid="cert-verify-page" className="pt-32">
      <section className="max-w-3xl mx-auto px-6 text-center mb-16">
        <ShieldCheck className="mx-auto text-gold mb-6" size={40} />
        <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Certificate & Warranty Portal</p>
        <h1 className="font-heading font-light text-5xl md:text-6xl leading-[1.05] mb-6">Provenance, verified.</h1>
        <p className="text-white/60 leading-relaxed">Enter your OPIKJEWELS certificate number to access full provenance, treatment disclosure, and lifetime warranty status.</p>

        <form onSubmit={verify} className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
          <input
            data-testid="verify-input"
            value={num} onChange={(e) => setNum(e.target.value)}
            placeholder="OPK-DIAM-001"
            className="bg-transparent border-b border-white/30 text-white px-2 py-3 focus:outline-none focus:border-gold font-accent tracking-[0.2em] uppercase text-sm w-full sm:w-96 placeholder-white/30 text-center"
          />
          <button data-testid="verify-submit" className="border border-gold text-gold font-accent uppercase tracking-[0.28em] text-xs px-9 py-4 hover:bg-gold hover:text-black transition-colors duration-500">Verify</button>
        </form>

        {error && <div data-testid="verify-error" className="mt-8 text-red-400 text-sm">{error}</div>}
      </section>

      {data && (
        <section data-testid="verify-result" className="max-w-4xl mx-auto px-6 pb-32">
          <div className="bg-onyx border border-gold/30 p-10">
            <div className="flex items-center gap-3 font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-6">
              <BadgeCheck size={18} /> Verified · Authentic OPIKJEWELS Specimen
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              {data.photo_url && (
                <div className="md:col-span-1">
                  <div className="aspect-square overflow-hidden bg-black border border-gold/30">
                    <img src={data.photo_url} alt={data.gemstone_name} className="w-full h-full object-cover" />
                  </div>
                </div>
              )}
              <div className={data.photo_url ? "md:col-span-2" : "md:col-span-3"}>
                <h2 className="font-heading text-5xl mb-2">{data.gemstone_name}</h2>
                <div className="text-white/50 mb-6 font-accent text-[10px] tracking-[0.28em] uppercase">Certificate № {data.certificate_number}</div>
                <a data-testid="download-coa-btn" href={`/api/certificates/${data.certificate_number}/coa.pdf`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-6 py-3 hover:bg-ivory">
                  Download Certificate (PDF)
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-y border-white/10 py-8">
              {[
                ["Category", data.category],
                ["Weight", `${data.carat} ${data.weight_unit || "ct"}`],
                ...(data.dimensions ? [["Dimensions", data.dimensions]] : []),
                ...(data.color ? [["Color", data.color]] : []),
                ["Origin", data.origin], ["Treatment", data.treatment],
                ["Issuing Lab", data.issuing_lab], ["Issued", data.issued_date],
                ["Owner", data.owner_name || "—"], ["Warranty", data.warranty_status],
                ["Insurance Eligible", data.insurance_eligible ? "Yes" : "No"],
                ["Signed By", `${data.signatory_name || "Mr. Tauf"} — ${data.signatory_title || "Authorized Officer"}`],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2">{k}</div>
                  <div className="font-heading text-xl text-ivory">{v}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="transfer-toggle" onClick={() => setTransferOpen(!transferOpen)} className="border border-gold text-gold font-accent uppercase tracking-[0.28em] text-xs px-7 py-4 hover:bg-gold hover:text-black transition-colors duration-500">Transfer Ownership</button>
            </div>

            {transferOpen && (
              <form onSubmit={submitTransfer} className="mt-8 space-y-4 border-t border-white/10 pt-8">
                <input data-testid="transfer-name" required placeholder="New Owner Full Name" value={transfer.new_owner_name} onChange={(e) => setTransfer({...transfer, new_owner_name: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold" />
                <input data-testid="transfer-email" required type="email" placeholder="New Owner Email" value={transfer.new_owner_email} onChange={(e) => setTransfer({...transfer, new_owner_email: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold" />
                <button data-testid="transfer-submit" className="bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-7 py-4 hover:bg-ivory">Submit Transfer (Sign in required)</button>
              </form>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default CertificateVerify;
